// ルート単位の結果カード。既定は折りたたみ（大規模プレビューで行数が爆発しないため）。
// 表示は相対パスと集計のみ。サンプルは上限つき。
import { useState } from 'react';
import { CaretRight, CaretDown } from '@phosphor-icons/react';

const STATUS = {
  pending: ['未実施', 'Pending', 'border-zinc-300 bg-zinc-50 text-zinc-600'],
  running: ['実行中', 'Running', 'border-sky-300 bg-sky-50 text-sky-700'],
  completed: ['完了', 'Completed', 'border-emerald-300 bg-emerald-50 text-emerald-700'],
  failed: ['失敗', 'Failed', 'border-rose-300 bg-rose-50 text-rose-700'],
  cancelled: ['中止', 'Cancelled', 'border-zinc-300 bg-zinc-50 text-zinc-600'],
  truncated: ['不完全', 'Truncated', 'border-amber-300 bg-amber-50 text-amber-800'],
};

const CONSISTENCY = {
  best_effort_stable: ['安定（最善努力）', 'Best-effort stable'],
  changed_during_enumeration: ['列挙中に変化', 'Changed during enumeration'],
  unverified: ['未検証', 'Unverified'],
};

export function NasRootCard({ root, index, sampleLimit = 25 }) {
  const [open, setOpen] = useState(false);
  const [ja, en, cls] = STATUS[root.status] || [root.status, root.status,
    'border-zinc-300 bg-zinc-50 text-zinc-600'];
  const [cja, cen] = CONSISTENCY[root.consistency] || CONSISTENCY.unverified;
  const samples = (root.samples || []).slice(0, sampleLimit);
  const extra = Math.max(0, (root.samples || []).length - samples.length);

  return (
    <div className="rounded border border-zinc-200 bg-white" data-testid={`nas-detail-root-${index}`}
      data-root-status={root.status} data-root-open={open ? 'true' : 'false'}>
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-2.5 py-2 flex items-start gap-2 hover:bg-zinc-50 rounded"
        aria-expanded={open} data-testid={`nas-detail-root-toggle-${index}`}>
        {open ? <CaretDown size={13} className="mt-0.5 shrink-0 text-zinc-500" />
          : <CaretRight size={13} className="mt-0.5 shrink-0 text-zinc-500" />}
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[12px] font-semibold text-zinc-800 break-all">{root.path}</span>
            <span className={`text-[10.5px] px-1.5 py-0.5 rounded-full border font-semibold ${cls}`}>
              {ja} / {en}
            </span>
            {root.pages > 0 && (
              <span className="text-[10.5px] text-zinc-500">{root.pages} ページ / pages</span>
            )}
          </span>
          <span className="mt-0.5 block text-[11px] text-zinc-600">
            検出 {root.files_observed} → 対象 {root.eligible_files}
            {' · '}新規 {root.new_files} / 変更 {root.changed_files} / 変更なし {root.unchanged_files}
          </span>
        </span>
      </button>

      {open && (
        <div className="px-2.5 pb-2 pt-0.5 border-t border-zinc-100"
          data-testid={`nas-detail-root-body-${index}`}>
          <p className="mt-1 text-[11px] text-zinc-600">
            除外: 拡張子 {root.unsupported_extensions} / サイズ {root.oversized_files}
            {' / '}隠し {root.hidden_files} / 機密 {root.guarded_files} / その他 {root.other_skips}
            {' · '}フォルダー {root.folders_observed}
          </p>
          <p className="mt-0.5 text-[11px] text-zinc-600">
            整合性 / Consistency: <b>{cja}</b> <span className="opacity-70">{cen}</span>
            {root.enumeration_id && (
              <span className="ml-1 font-mono opacity-60">#{String(root.enumeration_id).slice(0, 8)}</span>
            )}
          </p>
          {root.typed_error && (
            <p className="mt-1 text-[11px] text-rose-700">
              {root.typed_error_message_ja}
              <span className="ml-1 font-mono opacity-80">({root.typed_error})</span>
              {root.typed_error_message_en && (
                <span className="block opacity-80">{root.typed_error_message_en}</span>
              )}
            </p>
          )}
          {root.truncated && (
            <p className="mt-1 text-[11px] text-amber-800">
              上限到達のため不完全 / Truncated
              <span className="ml-1 font-mono">({root.truncation_reason})</span>
            </p>
          )}
          {samples.length > 0 && (
            <>
              <p className="mt-1.5 text-[11px] font-semibold text-zinc-600">
                スキップの例 <span className="font-normal opacity-70">Skipped samples</span>
              </p>
              <ul className="mt-0.5 ml-2 space-y-0.5" data-testid={`nas-detail-rootsamples-${index}`}>
                {samples.map((s) => (
                  <li key={s.path} className="text-[10.5px] text-zinc-600 break-all">
                    <span className="font-mono">{s.name}</span> — {s.reason_label}
                  </li>
                ))}
              </ul>
              <p className="mt-0.5 text-[10.5px] text-zinc-400">
                {samples.length} 件を表示（1 ルートあたり最大 {sampleLimit} 件）
                {extra > 0 ? ` · 保存済みの残り ${extra} 件は表示しません` : ''}
                <span className="block">Showing {samples.length}; at most {sampleLimit} per root.</span>
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default NasRootCard;
